import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft } from 'lucide-react';
import { client } from '@/lib/api';
import { formatCents } from '@/lib/format';
import type { QuoteWithDetails, PackageWithDetails, AddOn } from '@everplan/shared';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function computePackageTotal(
  pkg: PackageWithDetails,
  addOns: AddOn[],
  selectedAddOnIds: Set<string>,
  quoteCharges: Array<{ amount: number }> = [],
): number {
  const lineItemTotal = pkg.lineItems.reduce(
    (sum, li) => sum + (li.quantity ?? 1) * li.unitCost,
    0,
  );
  const chargeTotal = pkg.additionalCharges.reduce((sum, ch) => sum + ch.amount, 0);
  const addOnTotal = addOns
    .filter((ao) => selectedAddOnIds.has(ao.id))
    .reduce((sum, ao) => sum + (ao.quantity ?? 1) * ao.unitCost, 0);
  const quoteChargeTotal = quoteCharges.reduce((sum, ch) => sum + ch.amount, 0);
  return lineItemTotal + chargeTotal + addOnTotal + quoteChargeTotal;
}

function getSelectedAddOnIds(pkg: PackageWithDetails): Set<string> {
  return new Set(pkg.addOnSelections.map((s) => s.addOnId));
}

// Group line items by section for display
function groupBySection(lineItems: PackageWithDetails['lineItems']): Map<string, typeof lineItems> {
  const map = new Map<string, typeof lineItems>();
  for (const li of lineItems) {
    const key = li.section ?? 'Included';
    if (!map.has(key)) map.set(key, []);
    map.get(key)!.push(li);
  }
  return map;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface PackageColumnProps {
  quote: QuoteWithDetails;
  pkg: PackageWithDetails;
  total: number;
  isLowest?: boolean;
  onToggleAddOn: (addOnId: string, selected: boolean) => void;
  isMutating: boolean;
}

function PackageColumn({ quote, pkg, total, isLowest, onToggleAddOn, isMutating }: PackageColumnProps) {
  const selectedIds = getSelectedAddOnIds(pkg);
  const sections = groupBySection(pkg.lineItems);

  return (
    <div
      className={`flex flex-col min-w-[220px] rounded-2xl border p-4 gap-4 ${
        isLowest ? 'border-primary bg-primary/5' : 'border-border'
      }`}
    >
      {/* Header */}
      <div>
        <p className="text-xs label-caps text-muted-foreground">{quote.name}</p>
        <p className="font-semibold text-base mt-0.5">{pkg.name}</p>
      </div>

      {/* Line items by section */}
      <div className="space-y-3 flex-1">
        {sections.size === 0 && (
          <p className="text-sm text-muted-foreground italic">No line items</p>
        )}
        {Array.from(sections.entries()).map(([section, items]) => (
          <div key={section}>
            <p className="text-xs font-medium text-muted-foreground mb-1">{section}</p>
            <div className="space-y-1">
              {items.map((li) => (
                <div key={li.id} className="flex justify-between gap-2 text-sm">
                  <span className="text-foreground/80">
                    {li.name}
                    {li.quantity != null && li.quantity !== 1 && (
                      <span className="text-muted-foreground"> ×{li.quantity}</span>
                    )}
                  </span>
                  <span className="shrink-0 text-muted-foreground">
                    {formatCents(li.unitCost)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Additional charges */}
        {pkg.additionalCharges.length > 0 && (
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Charges</p>
            <div className="space-y-1">
              {pkg.additionalCharges.map((ch) => (
                <div key={ch.id} className="flex justify-between gap-2 text-sm">
                  <span className="text-foreground/80 capitalize">
                    {ch.description ?? ch.type}
                  </span>
                  <span className="shrink-0 text-muted-foreground">{formatCents(ch.amount)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add-ons */}
      {quote.addOns.length > 0 && (
        <div>
          <p className="text-xs font-medium text-muted-foreground mb-1">Add-ons</p>
          <div className="space-y-1.5">
            {quote.addOns.map((ao) => {
              const checked = selectedIds.has(ao.id);
              return (
                <label
                  key={ao.id}
                  className="flex items-center gap-2 text-sm cursor-pointer select-none"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={isMutating}
                    onChange={() => onToggleAddOn(ao.id, !checked)}
                    className="h-4 w-4 rounded border-border accent-primary"
                  />
                  <span className="flex-1 text-foreground/80">{ao.name}</span>
                  <span className="shrink-0 text-muted-foreground">
                    +{formatCents((ao.quantity ?? 1) * ao.unitCost)}
                  </span>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Footer total */}
      <div
        className={`flex justify-between items-center pt-3 border-t font-semibold ${
          isLowest ? 'border-primary/30 text-primary' : 'border-border'
        }`}
      >
        <span className="text-sm">Total</span>
        <span>{formatCents(total)}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Within-vendor view — one vendor with multiple packages side by side
// ---------------------------------------------------------------------------

interface WithinVendorViewProps {
  quote: QuoteWithDetails;
  onToggleAddOn: (quoteId: string, packageId: string, addOnId: string, selected: boolean) => void;
  isMutating: boolean;
}

function WithinVendorView({ quote, onToggleAddOn, isMutating }: WithinVendorViewProps) {
  const totals = quote.packages.map((pkg) =>
    computePackageTotal(pkg, quote.addOns, getSelectedAddOnIds(pkg), quote.additionalCharges),
  );
  const minTotal = totals.length > 0 ? Math.min(...totals) : null;

  return (
    <div>
      <p className="label-caps text-xs text-muted-foreground mb-3">{quote.name}</p>
      {quote.packages.length === 0 ? (
        <p className="text-sm text-muted-foreground italic">No packages for this quote.</p>
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-2">
          {quote.packages.map((pkg, i) => (
            <PackageColumn
              key={pkg.id}
              quote={quote}
              pkg={pkg}
              total={totals[i] ?? 0}
              isLowest={totals.length > 1 && totals[i] === minTotal}
              onToggleAddOn={(addOnId, selected) =>
                onToggleAddOn(quote.id, pkg.id, addOnId, selected)
              }
              isMutating={isMutating}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cross-vendor view — one column per quote, each with a package selector
// ---------------------------------------------------------------------------

interface CrossVendorViewProps {
  quotes: QuoteWithDetails[];
  onToggleAddOn: (quoteId: string, packageId: string, addOnId: string, selected: boolean) => void;
  isMutating: boolean;
}

function CrossVendorView({ quotes, onToggleAddOn, isMutating }: CrossVendorViewProps) {
  // Ephemeral state: selected package index per quote
  const [selectedPkgIndexes, setSelectedPkgIndexes] = useState<Record<string, number>>(
    () => Object.fromEntries(quotes.map((q) => [q.id, 0])),
  );

  const columns = quotes.map((quote) => {
    const pkgIdx = selectedPkgIndexes[quote.id] ?? 0;
    const pkg = quote.packages[pkgIdx] ?? null;
    return { quote, pkg };
  });

  const totals = columns.map(({ quote, pkg }) =>
    pkg ? computePackageTotal(pkg, quote.addOns, getSelectedAddOnIds(pkg), quote.additionalCharges) : 0,
  );
  const validTotals = totals.filter((t) => t > 0);
  const minTotal = validTotals.length > 0 ? Math.min(...validTotals) : null;

  return (
    <div className="flex gap-4 overflow-x-auto pb-2 items-start">
      {columns.map(({ quote, pkg }, i) => (
        <div key={quote.id} className="flex flex-col gap-2 min-w-[220px]">
          {/* Package selector dropdown */}
          {quote.packages.length > 1 && (
            <select
              value={selectedPkgIndexes[quote.id] ?? 0}
              onChange={(e) =>
                setSelectedPkgIndexes((prev) => ({
                  ...prev,
                  [quote.id]: Number(e.target.value),
                }))
              }
              className="w-full rounded-md border border-input bg-transparent px-3 py-1.5 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
            >
              {quote.packages.map((p, idx) => (
                <option key={p.id} value={idx}>
                  {p.name}
                </option>
              ))}
            </select>
          )}

          {pkg ? (
            <PackageColumn
              quote={quote}
              pkg={pkg}
              total={totals[i] ?? 0}
              isLowest={minTotal !== null && totals[i] === minTotal && validTotals.length > 1}
              onToggleAddOn={(addOnId, selected) =>
                onToggleAddOn(quote.id, pkg.id, addOnId, selected)
              }
              isMutating={isMutating}
            />
          ) : (
            <div className="rounded-2xl border border-border p-4 min-w-[220px]">
              <p className="text-xs label-caps text-muted-foreground">{quote.name}</p>
              <p className="text-sm text-muted-foreground italic mt-2">No packages</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

type CompareMode = 'within' | 'cross';

export default function CompareQuotesPage() {
  const { id: projectId, itemId } = useParams<{ id: string; itemId: string }>();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<CompareMode>('within');

  const { data: quotes, isLoading, isError } = useQuery({
    queryKey: ['quotes', itemId],
    queryFn: async () => {
      const res = await client.api.quotes.$get({ query: { itemId: itemId! } });
      if (!res.ok) throw new Error('Failed to fetch quotes');
      return res.json() as Promise<QuoteWithDetails[]>;
    },
    enabled: !!itemId,
  });

  const selectAddOn = useMutation({
    mutationFn: async ({
      packageId,
      addOnId,
    }: {
      quoteId: string;
      packageId: string;
      addOnId: string;
    }) => {
      const res = await client.api['add-on-selections'].$post({
        json: { packageId, addOnId },
      });
      if (!res.ok) throw new Error('Failed to select add-on');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotes', itemId] }),
  });

  const deselectAddOn = useMutation({
    mutationFn: async ({ packageId, addOnId }: { packageId: string; addOnId: string }) => {
      const res = await client.api['add-on-selections'][':packageId'][':addOnId'].$delete({
        param: { packageId, addOnId },
      });
      if (!res.ok) throw new Error('Failed to deselect add-on');
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['quotes', itemId] }),
  });

  const isMutating = selectAddOn.isPending || deselectAddOn.isPending;

  function handleToggleAddOn(
    quoteId: string,
    packageId: string,
    addOnId: string,
    selected: boolean,
  ) {
    if (selected) {
      selectAddOn.mutate({ quoteId, packageId, addOnId });
    } else {
      deselectAddOn.mutate({ packageId, addOnId });
    }
  }

  const backTo = `/projects/${projectId}?item=${itemId}`;

  // Quotes with multiple packages — valid for within-vendor view
  const multiPackageQuotes = (quotes ?? []).filter((q) => q.packages.length >= 2);

  return (
    <div className="space-y-8">
      {/* Back link */}
      <Link
        to={backTo}
        className="inline-flex items-center gap-1.5 label-caps text-xs text-muted-foreground hover:text-foreground transition-colors"
      >
        <ArrowLeft className="size-3.5" />
        Back to item
      </Link>

      {/* Header + mode toggle */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="display-tight text-3xl font-semibold">Compare Quotes</h1>
        <div className="flex rounded-lg border border-border overflow-hidden text-sm">
          <button
            onClick={() => setMode('within')}
            className={`px-4 py-1.5 font-medium transition-colors ${
              mode === 'within'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Within vendor
          </button>
          <button
            onClick={() => setMode('cross')}
            className={`px-4 py-1.5 font-medium transition-colors border-l border-border ${
              mode === 'cross'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            Cross vendor
          </button>
        </div>
      </div>

      {/* Loading / error states */}
      {isLoading && (
        <p className="text-muted-foreground text-sm">Loading quotes…</p>
      )}

      {isError && (
        <Card className="rounded-3xl shadow-ambient border-destructive/20">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">Failed to load quotes. Please try again.</p>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!isLoading && !isError && quotes && quotes.length === 0 && (
        <Card className="rounded-3xl shadow-ambient">
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground italic">
              No quotes yet.{' '}
              <Link to={backTo} className="underline hover:text-foreground">
                Add some from the item page.
              </Link>
            </p>
          </CardContent>
        </Card>
      )}

      {/* Within-vendor mode */}
      {!isLoading && !isError && quotes && quotes.length > 0 && mode === 'within' && (
        <div className="space-y-8">
          {multiPackageQuotes.length === 0 ? (
            <Card className="rounded-3xl shadow-ambient">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground italic">
                  No vendors have multiple packages to compare. Switch to{' '}
                  <Button
                    variant="link"
                    className="h-auto p-0 text-sm"
                    onClick={() => setMode('cross')}
                  >
                    cross-vendor
                  </Button>{' '}
                  mode to compare different vendors.
                </p>
              </CardContent>
            </Card>
          ) : (
            multiPackageQuotes.map((quote) => (
              <WithinVendorView
                key={quote.id}
                quote={quote}
                onToggleAddOn={handleToggleAddOn}
                isMutating={isMutating}
              />
            ))
          )}
        </div>
      )}

      {/* Cross-vendor mode */}
      {!isLoading && !isError && quotes && quotes.length > 0 && mode === 'cross' && (
        <div>
          {quotes.length < 2 ? (
            <Card className="rounded-3xl shadow-ambient">
              <CardContent className="pt-6">
                <p className="text-sm text-muted-foreground italic">
                  Add at least two quotes to compare across vendors.
                </p>
              </CardContent>
            </Card>
          ) : (
            <CrossVendorView
              quotes={quotes}
              onToggleAddOn={handleToggleAddOn}
              isMutating={isMutating}
            />
          )}
        </div>
      )}
    </div>
  );
}
